import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useEffect, useState, type FormEvent } from "react";

interface APITesterProps {
  defaultApiKey?: string | null;
}

export function APITester({ defaultApiKey = null }: APITesterProps) {
  const [endpoint, setEndpoint] = useState("/api/services/test-api/test");
  const [apiKey, setApiKey] = useState("");
  const [response, setResponse] = useState("");

  const testMutation = useMutation({
    mutationFn: async ({ endpoint, apiKey }: { endpoint: string; apiKey: string }) => {
      const url = new URL(endpoint, location.href);
      const headers = new Headers();
      if (apiKey.trim()) {
        headers.set("X-API-Key", apiKey.trim());
      }

      const res = await fetch(url, { method: "GET", headers });
      const text = await res.text();
      let formatted = text;
      try {
        formatted = JSON.stringify(JSON.parse(text), null, 2);
      } catch {
        // Keep raw text if not JSON.
      }
      return `HTTP ${res.status}\n${formatted}`;
    },
    onSuccess(result) {
      setResponse(result);
    },
    onError(error) {
      setResponse(String(error));
    },
  });

  useEffect(() => {
    if (defaultApiKey) {
      setApiKey(defaultApiKey);
    }
  }, [defaultApiKey]);

  const testEndpoint = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    await testMutation.mutateAsync({ endpoint, apiKey });
  };

  return (
    <div className="flex flex-col gap-6">
      <form onSubmit={testEndpoint} className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <Label htmlFor="endpoint">Endpoint</Label>
          <Input
            id="endpoint"
            type="text"
            name="endpoint"
            value={endpoint}
            onChange={e => setEndpoint(e.currentTarget.value)}
            placeholder="/api/services/test-api/test"
          />
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="api-key">API Key</Label>
          <Input
            id="api-key"
            type="text"
            name="apiKey"
            value={apiKey}
            onChange={e => setApiKey(e.currentTarget.value)}
            placeholder="ak_..."
            autoComplete="off"
          />
        </div>
        <Button type="submit" variant="secondary" disabled={testMutation.isPending}>
          {testMutation.isPending ? "Sending..." : "Send GET"}
        </Button>
      </form>
      <Label htmlFor="response">Response</Label>
      <Textarea
        id="response"
        readOnly
        value={response}
        placeholder="Response will appear here..."
        className="min-h-[180px] font-mono resize-y"
      />
    </div>
  );
}
